/**
 * COP Streams tab — Hollywood-squares grid of active live feeds.
 * Subscribes only while the tab is active; tiles can locate / pin on the map.
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import LiveStreamPlayer from './LiveStreamPlayer';
import type { DeviceLocationStatus } from '../services/copApi';

const MAX_TILES = 9;

interface LiveStreamsTabProps {
  devices: DeviceLocationStatus[];
  /** True only while the Streams tab is selected — gates LiveKit connects. */
  isActive: boolean;
  onLocateDevice: (device: DeviceLocationStatus) => void;
  onWatchOnMap: (device: DeviceLocationStatus) => void;
  selectedDeviceId?: string | null;
}

const LiveStreamsTab: React.FC<LiveStreamsTabProps> = ({
  devices,
  isActive,
  onLocateDevice,
  onWatchOnMap,
  selectedDeviceId,
}) => {
  const [audioMode, setAudioMode] = useState<'all' | 'selected'>('selected');

  const streaming = useMemo(
    () =>
      devices.filter(
        (d) =>
          d.is_streaming &&
          Number.isFinite(d.latitude) &&
          Number.isFinite(d.longitude)
      ),
    [devices]
  );

  const visible = streaming.slice(0, MAX_TILES);
  const overflow = Math.max(0, streaming.length - visible.length);

  const cols = visible.length <= 1 ? 1 : visible.length <= 4 ? 2 : 3;

  if (!isActive) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Open this tab to connect live feeds.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VideocamIcon sx={{ color: streaming.length ? '#ef4444' : 'text.secondary', fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Live Streams
          </Typography>
          <Chip
            size="small"
            label={`${streaming.length} live`}
            sx={{
              height: 22,
              fontWeight: 700,
              bgcolor: streaming.length ? 'rgba(220,38,38,0.2)' : 'action.hover',
              color: streaming.length ? '#fca5a5' : 'text.secondary',
            }}
          />
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={audioMode}
          onChange={(_e, v) => {
            if (v) setAudioMode(v);
          }}
          sx={{
            '& .MuiToggleButton-root': {
              py: 0.25,
              px: 1,
              fontSize: '0.65rem',
              textTransform: 'none',
            },
          }}
        >
          <ToggleButton value="selected">Audio: focused</ToggleButton>
          <ToggleButton value="all">Audio: all</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Typography variant="caption" color="text.secondary">
        Feeds connect only while this tab is open. Use locate to fly the map, pin to attach the map LIVE pip.
        Record downloads a WebM clip to your machine.
      </Typography>

      {streaming.length === 0 && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          No devices are streaming right now. Start Live on a phone to populate this grid.
        </Alert>
      )}

      {overflow > 0 && (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          Showing {MAX_TILES} of {streaming.length} streams (small-team grid cap). Focus priority devices on the map.
        </Alert>
      )}

      {visible.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            gap: 1,
            flex: 1,
            minHeight: 280,
            alignContent: 'start',
          }}
        >
          {visible.map((device) => {
            const focused = selectedDeviceId === device.device_id;
            const muteOthers = audioMode === 'selected' && !!selectedDeviceId && !focused;
            return (
              <Box
                key={device.device_id}
                sx={{
                  minHeight: cols === 1 ? 280 : 160,
                  height: cols === 1 ? 320 : 'clamp(150px, 22vh, 220px)',
                  borderRadius: 1,
                  outline: focused ? '2px solid #4caf50' : 'none',
                  outlineOffset: 1,
                  cursor: 'pointer',
                  overflow: 'hidden',
                }}
                onClick={() => onLocateDevice(device)}
              >
                <LiveStreamPlayer
                  variant="tile"
                  enabled={isActive}
                  muted={muteOthers}
                  deviceId={device.device_id}
                  label={device.entity_id || device.device_id}
                  onLocate={() => onLocateDevice(device)}
                  onWatchOnMap={() => onWatchOnMap(device)}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default LiveStreamsTab;
